/**
 * Pet-owner "Profile & Settings" page (`/app/profile`) — sectioned deep profile:
 * personal + avatar, saved addresses, emergency contact, notification
 * preferences, and settings.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  LogOut,
  MapPin,
  Moon,
  Plus,
  Star,
  Sun,
  Trash2,
  Upload,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { TextField, TextAreaField } from '@/components/text-field';
import { AuthImage } from '@/components/auth-image';
import { ApiError } from '@/lib/api';
import { use_session_store } from '@/lib/session-store';
import { use_theme_store } from '@/lib/theme-store';
import {
  use_user_profile,
  use_update_user_profile,
  use_upload_avatar,
} from '@/features/user-profile/hooks';
import { use_addresses, use_address_mutations } from '@/features/user-address/hooks';
import { AccountDeletionCard } from '@/components/account-deletion-card';
import type { UserAddress, AddressInput } from '@/features/user-address/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_RE = /^[1-9][0-9]{5}$/;

export function UserProfilePage() {
  const profile_q = use_user_profile();

  return (
    <div data-testid="user-profile" className="space-y-6">
      <SectionHeader
        title="Profile & Settings"
        description="Manage your account details and preferences."
      />

      {profile_q.isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-40 rounded-card" />
          <Skeleton className="h-40 rounded-card" />
        </div>
      ) : profile_q.isError ? (
        <ErrorState
          title="Couldn't load your profile"
          action={
            <Button variant="secondary" size="sm" onClick={() => profile_q.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <>
          <PersonalSection />
          <AddressesSection />
          <EmergencySection />
          <NotificationsSection />
          <SettingsSection />
          {/* Last on the page on purpose: it is the one control here that ends the account. */}
          <AccountDeletionCard role="user" />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-5 p-6">
        <div className="flex items-center justify-between gap-3">
          <p className="font-heading text-base font-bold">{title}</p>
          {action}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Saved({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="text-sm font-medium text-primary" data-testid="section-saved">
      Saved
    </span>
  );
}

// ---------------------------------------------------------------------------
// Personal + avatar
// ---------------------------------------------------------------------------

function PersonalSection() {
  const { data: p } = use_user_profile();
  const update = use_update_user_profile();
  const avatar = use_upload_avatar();
  const phone = use_session_store((s) => s.phone);
  const file_ref = useRef<HTMLInputElement>(null);

  const [first_name, set_first] = useState('');
  const [last_name, set_last] = useState('');
  const [email, set_email] = useState('');
  const [email_error, set_email_error] = useState<string>();
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_first(p.first_name ?? '');
    set_last(p.last_name ?? '');
    set_email(p.email ?? '');
  }, [p]);

  const dirty =
    !!p &&
    (first_name !== (p.first_name ?? '') ||
      last_name !== (p.last_name ?? '') ||
      email !== (p.email ?? ''));
  const email_invalid = email.trim().length > 0 && !EMAIL_RE.test(email.trim());

  function submit(e: FormEvent) {
    e.preventDefault();
    set_email_error(undefined);
    set_saved(false);
    if (email_invalid) return set_email_error('Enter a valid email address');
    update.mutate(
      { first_name, last_name, email },
      {
        onSuccess: () => set_saved(true),
        onError: (err) =>
          set_email_error(
            err instanceof ApiError && err.error_code === 'EMAIL_TAKEN'
              ? 'That email is already in use'
              : 'Could not save',
          ),
      },
    );
  }

  return (
    <SectionCard title="Personal" action={<Saved show={saved && !dirty} />}>
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary shadow-clay-sm">
          {p?.avatar_url ? (
            <AuthImage
              path={p.avatar_url}
              alt="Your avatar"
              className="h-full w-full object-cover"
              data-testid="profile-avatar-img"
            />
          ) : (
            <UserRound className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Signed in as
          </p>
          <p className="font-heading text-lg font-bold" data-testid="profile-phone">
            {phone ?? '—'}
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-1 w-fit gap-2"
            onClick={() => file_ref.current?.click()}
            disabled={avatar.isPending}
            data-testid="profile-avatar-upload"
          >
            {avatar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {p?.avatar_url ? 'Change photo' : 'Add photo'}
          </Button>
          <input
            ref={file_ref}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid="profile-avatar-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) avatar.mutate(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="First name"
            name="first_name"
            optional
            value={first_name}
            onChange={(e) => set_first(e.target.value)}
            data-testid="profile-first-name"
          />
          <TextField
            label="Last name"
            name="last_name"
            optional
            value={last_name}
            onChange={(e) => set_last(e.target.value)}
            data-testid="profile-last-name"
          />
        </div>
        <TextField
          label="Email"
          name="email"
          type="email"
          optional
          value={email}
          onChange={(e) => {
            set_email(e.target.value);
            set_email_error(undefined);
            set_saved(false);
          }}
          error={email_error}
          data-testid="profile-email"
        />
        <Button
          type="submit"
          variant="gradient"
          className="w-fit"
          disabled={!dirty || email_invalid || update.isPending}
          data-testid="profile-save"
        >
          {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save changes
        </Button>
      </form>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Saved addresses
// ---------------------------------------------------------------------------

const EMPTY_ADDRESS: AddressInput = {
  label: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  landmark: '',
  phone: '',
  access_notes: '',
};

function AddressesSection() {
  const addresses_q = use_addresses();
  const { create, update, remove, set_default } = use_address_mutations();
  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<AddressInput>(EMPTY_ADDRESS);
  const [errors, set_errors] = useState<Record<string, string>>({});

  const rows = addresses_q.data ?? [];

  function begin_add() {
    set_form(EMPTY_ADDRESS);
    set_errors({});
    set_editing({});
  }
  function begin_edit(a: UserAddress) {
    set_form({
      label: a.label,
      address: a.address,
      city: a.city,
      state: a.state,
      pincode: a.pincode,
      landmark: a.landmark ?? '',
      phone: a.phone ?? '',
      access_notes: a.access_notes ?? '',
    });
    set_errors({});
    set_editing({ id: a.id });
  }
  function upd<K extends keyof AddressInput>(k: K, v: AddressInput[K]) {
    set_form((f) => ({ ...f, [k]: v }));
  }
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (form.label.trim().length < 1) e.label = 'Required.';
    if (form.address.trim().length < 5) e.address = 'At least 5 characters.';
    if (form.city.trim().length < 2) e.city = 'Required.';
    if (form.state.trim().length < 2) e.state = 'Required.';
    if (!PINCODE_RE.test(form.pincode.trim())) e.pincode = '6-digit pincode.';
    return e;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length) return set_errors(v);
    const landmark = form.landmark?.trim();
    const phone = form.phone?.trim();
    const access_notes = form.access_notes?.trim();
    const base: AddressInput = {
      label: form.label.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
    };
    const done = () => set_editing(null);
    if (editing?.id) {
      // On update the backend accepts null to clear an optional field.
      update.mutate(
        {
          id: editing.id,
          input: {
            ...base,
            landmark: landmark || null,
            phone: phone || null,
            access_notes: access_notes || null,
          },
        },
        { onSuccess: done },
      );
    } else {
      // On create the backend only accepts an optional field when present (omit when empty).
      create.mutate(
        {
          ...base,
          ...(landmark ? { landmark } : {}),
          ...(phone ? { phone } : {}),
          ...(access_notes ? { access_notes } : {}),
        },
        { onSuccess: done },
      );
    }
  }

  return (
    <SectionCard
      title="Saved addresses"
      action={
        !editing && (
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5"
            onClick={begin_add}
            data-testid="address-add"
          >
            <Plus className="h-4 w-4" /> Add
          </Button>
        )
      }
    >
      {editing ? (
        <form
          onSubmit={submit}
          className="flex flex-col gap-4 rounded-card border-2 border-brand-outline-variant/20 p-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Label"
              name="label"
              placeholder="Home"
              value={form.label}
              onChange={(e) => upd('label', e.target.value)}
              error={errors.label}
              data-testid="address-label-input"
            />
            <TextField
              label="Landmark"
              name="landmark"
              optional
              value={form.landmark ?? ''}
              onChange={(e) => upd('landmark', e.target.value)}
              data-testid="address-landmark-input"
            />
          </div>
          <TextAreaField
            label="Address"
            name="address"
            rows={2}
            value={form.address}
            onChange={(e) => upd('address', e.target.value)}
            error={errors.address}
            data-testid="address-line-input"
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              label="City"
              name="city"
              value={form.city}
              onChange={(e) => upd('city', e.target.value)}
              error={errors.city}
              data-testid="address-city-input"
            />
            <TextField
              label="State"
              name="state"
              value={form.state}
              onChange={(e) => upd('state', e.target.value)}
              error={errors.state}
              data-testid="address-state-input"
            />
            <TextField
              label="Pincode"
              name="pincode"
              inputMode="numeric"
              value={form.pincode}
              onChange={(e) => upd('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
              error={errors.pincode}
              data-testid="address-pincode-input"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Phone number"
              name="phone"
              optional
              inputMode="tel"
              placeholder="+91…"
              value={form.phone ?? ''}
              onChange={(e) => upd('phone', e.target.value)}
              data-testid="address-phone-input"
            />
            <TextField
              label="Access notes"
              name="access_notes"
              optional
              placeholder="Gate code, floor, where to meet…"
              value={form.access_notes ?? ''}
              onChange={(e) => upd('access_notes', e.target.value)}
              data-testid="address-access-notes-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              variant="gradient"
              disabled={create.isPending || update.isPending}
              data-testid="address-save"
            >
              {(create.isPending || update.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Save address
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="addresses-empty">
          No saved addresses yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="addresses-list">
          {rows.map((a) => (
            <div
              key={a.id}
              data-testid={`address-card-${a.id}`}
              className="flex items-start justify-between gap-3 rounded-card border-2 border-brand-outline-variant/20 p-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-primary" />
                  <span className="font-semibold">{a.label}</span>
                  {a.is_default && (
                    <StatusPill tone="success" data-testid={`address-default-${a.id}`}>
                      Default
                    </StatusPill>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {a.address}
                  {a.landmark ? `, ${a.landmark}` : ''}, {a.city}, {a.state} {a.pincode}
                </p>
                {a.phone && (
                  <p className="mt-0.5 text-sm text-muted-foreground" data-testid={`address-phone-${a.id}`}>
                    ☎ {a.phone}
                  </p>
                )}
                {a.access_notes && (
                  <p className="mt-0.5 text-sm text-muted-foreground" data-testid={`address-access-notes-${a.id}`}>
                    {a.access_notes}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {!a.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => set_default.mutate(a.id)}
                      data-testid={`address-set-default-${a.id}`}
                    >
                      <Star className="h-3.5 w-3.5" /> Make default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => begin_edit(a)}
                    data-testid={`address-edit-${a.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-muted-foreground hover:text-destructive"
                    onClick={() => remove.mutate(a.id)}
                    data-testid={`address-delete-${a.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Emergency contact
// ---------------------------------------------------------------------------

function EmergencySection() {
  const { data: p } = use_user_profile();
  const update = use_update_user_profile();
  const [name, set_name] = useState('');
  const [rel, set_rel] = useState('');
  const [phone, set_phone] = useState('');
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_name(p.emergency_contact_name ?? '');
    set_rel(p.emergency_contact_relationship ?? '');
    set_phone(p.emergency_contact_phone ?? '');
  }, [p]);

  const dirty =
    !!p &&
    (name !== (p.emergency_contact_name ?? '') ||
      rel !== (p.emergency_contact_relationship ?? '') ||
      phone !== (p.emergency_contact_phone ?? ''));

  function submit(e: FormEvent) {
    e.preventDefault();
    set_saved(false);
    update.mutate(
      {
        emergency_contact_name: name,
        emergency_contact_relationship: rel,
        emergency_contact_phone: phone,
      },
      { onSuccess: () => set_saved(true) },
    );
  }

  return (
    <SectionCard title="Emergency contact" action={<Saved show={saved && !dirty} />}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            label="Name"
            name="ec_name"
            optional
            value={name}
            onChange={(e) => set_name(e.target.value)}
            data-testid="emergency-name"
          />
          <TextField
            label="Relationship"
            name="ec_rel"
            optional
            value={rel}
            onChange={(e) => set_rel(e.target.value)}
            data-testid="emergency-relationship"
          />
          <TextField
            label="Phone"
            name="ec_phone"
            optional
            value={phone}
            onChange={(e) => set_phone(e.target.value)}
            data-testid="emergency-phone"
          />
        </div>
        <Button
          type="submit"
          variant="gradient"
          className="w-fit"
          disabled={!dirty || update.isPending}
          data-testid="emergency-save"
        >
          {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </form>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Notification preferences (auto-save toggles)
// ---------------------------------------------------------------------------

type NotifKey = 'notify_sms' | 'notify_email' | 'notify_push' | 'marketing_opt_in';

function NotificationsSection() {
  const { data: p } = use_user_profile();
  const update = use_update_user_profile();

  // Local mirror so toggles flip instantly (the PATCH persists in the background).
  const [prefs, set_prefs] = useState<Record<NotifKey, boolean>>({
    notify_sms: true,
    notify_email: true,
    notify_push: true,
    marketing_opt_in: false,
  });
  useEffect(() => {
    if (!p) return;
    set_prefs({
      notify_sms: p.notify_sms,
      notify_email: p.notify_email,
      notify_push: p.notify_push,
      marketing_opt_in: p.marketing_opt_in,
    });
  }, [p]);

  const rows: { key: NotifKey; label: string; hint: string }[] = [
    { key: 'notify_sms', label: 'SMS', hint: 'Booking + service updates by text' },
    { key: 'notify_email', label: 'Email', hint: 'Receipts and summaries' },
    { key: 'notify_push', label: 'Push', hint: 'Alerts on your phone' },
    { key: 'marketing_opt_in', label: 'Offers & tips', hint: 'Occasional marketing' },
  ];

  return (
    <SectionCard title="Notifications">
      <div className="flex flex-col divide-y-2 divide-brand-outline-variant/15">
        {rows.map((r) => (
          <label
            key={r.key}
            className="flex cursor-pointer items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
          >
            <span>
              <span className="block text-sm font-semibold">{r.label}</span>
              <span className="block text-xs text-muted-foreground">{r.hint}</span>
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 rounded border-input accent-primary"
              data-testid={`toggle-${r.key}`}
              checked={prefs[r.key]}
              onChange={(e) => {
                const checked = e.target.checked;
                set_prefs((prev) => ({ ...prev, [r.key]: checked }));
                update.mutate({ [r.key]: checked });
              }}
            />
          </label>
        ))}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

function SettingsSection() {
  const navigate = useNavigate();
  const clear = use_session_store((s) => s.clear);
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);

  function sign_out() {
    navigate('/', { replace: true });
    clear();
  }

  return (
    <SectionCard title="Settings">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold">Appearance</p>
          <p className="text-xs text-muted-foreground">{dark ? 'Dark mode' : 'Light mode'}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="gap-2"
          onClick={toggle_theme}
          data-testid="profile-theme-toggle"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {dark ? 'Switch to light' : 'Switch to dark'}
        </Button>
      </div>
      <div className="border-t-2 border-brand-outline-variant/20 pt-4">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 text-muted-foreground hover:text-foreground"
          onClick={sign_out}
          data-testid="profile-sign-out"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </SectionCard>
  );
}
